// 18's settings registration — the account's Orpheus switch, as a pane in module 22's Account group.
// v3 rendered it as a section INSIDE `AccountSettings.vue`; v4's Account pane is 22's and 22 never
// imports a registrant, so the switch takes its own route and nav entry through the same registrar
// 11, 12, 20, 23 and 30 use (contract 22 §5/§8). `app/bootstrap.ts` hands the registrar in.
//
// A function, not a module side effect: `resetSettingsRegistry()` clears nav entries wholesale, so
// only a registration a test can re-run survives.
import type { RouteRecordRaw } from 'vue-router'

import { useOrpheusRbac } from './composables/useOrpheusRbac'
import { registerOrpheusMessages } from './i18n'

import { useSettingsRbac, type SettingsNavEntry } from '@/features/workspace-settings'
import { useLocaleStore } from '@/shared/i18n'

export interface OrpheusSettingsRegistrar {
  registerNav: (entry: SettingsNavEntry) => void
  registerRoutes: (records: RouteRecordRaw[]) => void
}

export const ORPHEUS_SETTINGS_NAV_KEY = 'orpheus'
/** Where a key-error card's "Open key settings" goes (v3 linked Account Settings' basics). */
export const ORPHEUS_SETTINGS_ROUTE_NAME = 'SettingsOrpheus'

export const orpheusSettingsRoutes: RouteRecordRaw[] = [
  {
    path: 'account/orpheus',
    name: ORPHEUS_SETTINGS_ROUTE_NAME,
    component: () => import('./components/settings/OrpheusSettingsPane.vue'),
    meta: { titleKey: 'orpheus.consentSettings.title', navKey: ORPHEUS_SETTINGS_NAV_KEY },
    beforeEnter: () => registerOrpheusMessages(useLocaleStore().locale),
  },
]

/**
 * Routes first — `registerSettingsChildRoutes` throws once the router is sealed, so a mis-ordered
 * composition root fails on the loud half. Visible to an account admin on an account that has
 * Orpheus at all (v3 `isAdminForSelectedAccount`); anyone else deep-linking sees the state
 * read-only, because the status read is open to every member (§12 defence in depth).
 */
export function installOrpheusSettings(reg: OrpheusSettingsRegistrar): void {
  reg.registerRoutes(orpheusSettingsRoutes)
  reg.registerNav({
    key: ORPHEUS_SETTINGS_NAV_KEY,
    labelKey: 'orpheus.nav.settings',
    icon: 'orpheus',
    group: 'account',
    order: 40,
    routeName: ORPHEUS_SETTINGS_ROUTE_NAME,
    visible: () => useSettingsRbac().canManageAccount.value && useOrpheusRbac().canUseOrpheus.value,
  })
}
